/**
 * Onboarding Progress Provider
 *
 * Manages onboarding steps state with API integration and polling.
 * Automatically fetches steps when thread is created and polls for updates.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { useAppDispatch, useAppSelector } from '@/stores/store';
import {
  setSteps,
  setLoading,
  setLastFetchedThreadId,
  setSubdomain,
  resetSteps,
} from '@/stores/slices/onboarding-steps-slice';
import { setTeamLeader } from '@/stores/slices/chat-slice';
import { selectCustomEventValues } from '@/stores/selectors/chat-selectors';
import {
  fetchOnboardingSteps,
  fetchDefaultOnboardingSteps,
} from '@/services/onboarding-steps.service';
import type { OnboardingStep } from '@/types/onboarding.types';

/**
 * Number of retry attempts on failure
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Poll cadence (ms) for ``GET /agents/onboarding-agent/threads/{tid}/onboarding-steps``.
 * Mirrors the cleanup/migration providers so the three sidebars feel
 * consistent. Stops automatically when the terminal ``end`` step is Complete.
 */
const POLLING_INTERVAL = 5000;

interface OnboardingProgressContextValue {
  steps: OnboardingStep[];
  isLoading: boolean;
  currentStep: OnboardingStep | null;
  refetch: () => Promise<void>;
}

const defaultContextValue: OnboardingProgressContextValue = {
  steps: [],
  isLoading: false,
  currentStep: null,
  refetch: async () => {},
};

const OnboardingProgressContext = createContext<OnboardingProgressContextValue>(defaultContextValue);

interface OnboardingProgressProviderProps {
  children: ReactNode;
  threadId: string | null;
}

export function OnboardingProgressProvider({
  children,
  threadId,
}: OnboardingProgressProviderProps) {
  const dispatch = useAppDispatch();
  const { steps, isLoading, lastFetchedThreadId } = useAppSelector(
    (state) => state.onboardingSteps
  );
  const customEventValues = useAppSelector(selectCustomEventValues);

  const retryCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const lastProcessedEventRef = useRef<string | null>(null);
  const defaultStepsFetchedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Fetch steps with silent error retry
   */
  const fetchSteps = useCallback(async () => {
    if (!threadId) {
      dispatch(resetSteps());
      dispatch(setTeamLeader(null));
      return;
    }

    dispatch(setLoading(true));

    const [error, data] = await fetchOnboardingSteps(threadId);

    // Only update if component is still mounted
    if (!isMountedRef.current) return;

    if (error) {
      console.warn('Failed to fetch onboarding steps:', error);

      // Silent retry logic
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        retryCountRef.current += 1;
        setTimeout(
          () => {
            if (isMountedRef.current) {
              fetchSteps();
            }
          },
          2000 * retryCountRef.current
        ); // Exponential backoff
      }
    } else if (data) {
      dispatch(setSteps(data.steps));
      dispatch(setSubdomain(data.subdomain || null));
      dispatch(setLastFetchedThreadId(threadId));
      retryCountRef.current = 0; // Reset retry count on success
    }

    dispatch(setLoading(false));
  }, [threadId, dispatch]);

  /**
   * Effect: Subscribe to phase_update events and fetch when changes detected
   */
  useEffect(() => {
    // Skip if no thread
    if (!threadId) return;

    // Filter for phase_update events
    const phaseUpdateEvents = customEventValues.filter(
      (event) => event.type === 'phase_update'
    );

    // No phase_update events yet
    if (phaseUpdateEvents.length === 0) return;

    // Get the latest event
    const latestEvent = phaseUpdateEvents[phaseUpdateEvents.length - 1];
    const eventId = `${latestEvent.timestamp}-${latestEvent.name}`;

    // Skip if already processed
    if (lastProcessedEventRef.current === eventId) {
      return;
    }

    // Smart comparison: Check if this event indicates a change
    const eventStepName = latestEvent.name;
    const matchingStep = steps.find((step) => step.name === eventStepName);

    // Determine if fetch is needed:
    // 1. Step doesn't exist yet (new step) - FETCH
    // 2. Step exists - assume status might have changed - FETCH
    //    (We can't know if status changed without fetching, so we fetch to check)
    const shouldFetch = true; // Always fetch when phase_update arrives for now

    if (shouldFetch) {
      console.log(
        `🔄 [OnboardingProgress] phase_update for "${eventStepName}" - fetching steps`
      );
      fetchSteps();
      lastProcessedEventRef.current = eventId;
    } else {
      console.log(
        `⏭️ [OnboardingProgress] phase_update for "${eventStepName}" - no fetch needed`
      );
      lastProcessedEventRef.current = eventId;
    }
  }, [customEventValues, threadId, steps, fetchSteps]);

  /**
   * Effect: Fetch default steps on mount (when no thread exists)
   */
  useEffect(() => {
    const loadDefaultSteps = async () => {
      // Only fetch if no thread and not already fetched
      if (!threadId && !defaultStepsFetchedRef.current && isMountedRef.current) {
        dispatch(setLoading(true));

        const [error, data] = await fetchDefaultOnboardingSteps();

        if (!isMountedRef.current) return;

        if (error) {
          console.warn('Failed to fetch default onboarding steps:', error);
        } else if (data) {
          dispatch(setSteps(data.steps));
          dispatch(setSubdomain(data.subdomain || null));
          defaultStepsFetchedRef.current = true;
        }

        dispatch(setLoading(false));
      }
    };

    loadDefaultSteps();
  }, [threadId, dispatch]);

  /**
   * Effect: Handle thread changes
   */
  useEffect(() => {
    // Thread changed to null: reset to default steps
    if (!threadId) {
      dispatch(resetSteps());
      defaultStepsFetchedRef.current = false;
      return;
    }

    // Thread changed to new thread: fetch thread-specific steps
    if (threadId !== lastFetchedThreadId) {
      fetchSteps();
    }
  }, [threadId, lastFetchedThreadId, fetchSteps, dispatch]);

  /**
   * Effect: Poll for step updates while a thread is active.
   *
   * Mirrors ``cleanup-progress-provider`` and ``migration-progress-provider``:
   * 5-second tick, stop automatically when the terminal ``end`` step is
   * Complete, restart on thread change, clean up on unmount. This is the
   * mechanism that lets the sidebar advance in real time as the LLM calls
   * ``update_onboarding_step`` — the legacy ``phase_update`` custom-event
   * branch above still runs as a faster path when those events arrive, but
   * polling is the guarantee.
   */
  useEffect(() => {
    if (!threadId) return;

    pollingIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      const endStep = steps.find((s) => s.name === 'end');
      if (endStep?.status === 'Complete') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }
      fetchSteps();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, fetchSteps]);

  /**
   * Effect: Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * Calculate current step (first non-complete step)
   */
  const currentStep = steps.find((step) => step.status !== 'Complete') || null;

  const contextValue: OnboardingProgressContextValue = {
    steps,
    isLoading,
    currentStep,
    refetch: fetchSteps,
  };

  return (
    <OnboardingProgressContext.Provider value={contextValue}>
      {children}
    </OnboardingProgressContext.Provider>
  );
}

/**
 * Hook to access onboarding progress context
 */
export function useOnboardingProgress(): OnboardingProgressContextValue {
  return useContext(OnboardingProgressContext);
}
